import { DEFAULT_ENCOUNTER_TEMPLATE_IDS } from '../data/enemies';
import {
  applyOutgoingDamageBonuses,
  bleedDamageForStacks,
  defaultCharacterCombatStats,
  mitigateIncomingDamage,
  rollCritDamage,
  type CharacterCombatStats,
} from './characterStats';
import {
  consecutiveCritBonusPp,
  createAttackSequenceCritState,
  recordAttackCritOutcome,
} from './attackSequence';
import {
  chooseDefaultCombo,
  findEligibleCombos,
  getComboById,
  combineComboAndClassFlatAttack,
  computeComboAttack,
} from './combinations';
import {
  clearDiceFaces,
  createDiceTray,
  rerollSingleDie,
  rerollUnheldDice,
  rollUnlockedDice,
  toggleDieHold,
} from './dice';
import {
  createEnemyFromTemplate,
  livingEnemies,
  refreshEnemyIntention,
} from './enemies';
import {
  acceptSkillOffer,
  createTemporaryProgress,
  gainTemporaryXp,
  rerollSkillOffers,
  rollSkillOffers,
  xpForEnemyTemplate,
} from './progression';
import type { SeededRng } from './rng';
import {
  applyCleaveSplash,
  applyStatusToEnemy,
  executePrimarySkill,
  getSkillDefinition,
  planParticipatingSkills,
} from './skills';
import { formatPlayerAttackLog } from './combatLog';
import {
  PROVISIONAL_PLAYER_MAX_HP,
  REROLL_ALL_PER_TURN,
  REROLL_SINGLE_PER_TURN,
  STARTING_UNLOCKED_DICE_SLOTS,
  type CombatEvent,
  type ComboId,
  type EncounterState,
  type EnemyState,
  type StatusInstance,
} from './types';

export interface CreateEncounterOptions {
  unlockedDiceSlots?: number;
  enemyTemplateIds?: readonly string[];
  playerName?: string;
  playerMaxHp?: number;
  /**
   * Carry current HP into the next dungeon encounter.
   * Ordinary encounters do not restore HP between fights.
   */
  playerHp?: number;
  /** Carry temporary XP / skill offers across encounters in a run. */
  progress?: EncounterState['progress'];
  /** Carry dice unlocks + assigned primary skills across encounters. */
  tray?: EncounterState['tray'];
  /** Permanent active unlocks for this run's skill offers. */
  unlockedActiveSkillIds?: readonly string[];
  /** Shared character combat stats snapshot for this run. */
  combatStats?: CharacterCombatStats;
  /** Dungeon-entry equipment snapshot for offer eligibility (M9-A). */
  equipmentSnapshot?: import('./equipmentCombat').EquipmentSnapshot | null;
  /** M7-D2 dungeon progression — scales enemies. Defaults to level 1 Standard. */
  dungeonLevel?: number;
  mutation?: 'standard' | 'hard' | 'brutal';
}

function pushLog(state: EncounterState, line: string): EncounterState {
  return { ...state, log: [...state.log, line] };
}

function withCombos(state: EncounterState): EncounterState {
  const swing = state.combatStats?.weaponSwingDamage ?? 0;
  const flatAttack = state.combatStats?.flatAttack ?? 0;
  const eligibleCombos = findEligibleCombos(state.tray, swing, flatAttack);
  const defaultCombo = chooseDefaultCombo(eligibleCombos);
  return {
    ...state,
    eligibleCombos,
    selectedComboId: defaultCombo?.definition.id ?? null,
  };
}

function defaultTargetId(enemies: EnemyState[]): string | null {
  const living = livingEnemies(enemies);
  return living[0]?.id ?? null;
}

function tickStatuses(statuses: StatusInstance[]): StatusInstance[] {
  return statuses
    .map((s) => ({ ...s, remainingTurns: s.remainingTurns - 1 }))
    .filter((s) => s.remainingTurns > 0);
}

/**
 * Spec bleed: damage at START of enemy turn, then EXACTLY ONE stack expires.
 * intensity = stack count. Bleed damage bonus % from talents applies.
 */
function applyBleedTicks(
  enemies: EnemyState[],
  events: CombatEvent[],
  log: string[],
  bleedDamageBonusPct = 0,
): EnemyState[] {
  return enemies.map((enemy) => {
    if (enemy.hp <= 0) {
      return enemy;
    }
    const bleed = enemy.statuses.find((s) => s.id === 'bleed');
    if (!bleed || bleed.intensity <= 0) {
      return enemy;
    }
    const stacks = bleed.intensity;
    const damage = bleedDamageForStacks(
      stacks,
      enemy.maxHp,
      bleedDamageBonusPct,
    );
    const hpAfter = Math.max(0, enemy.hp - damage);
    events.push({
      type: 'status_tick',
      enemyId: enemy.id,
      statusId: 'bleed',
      damage,
      targetHpAfter: hpAfter,
    });
    log.push(`${enemy.name} bleeds for ${damage} (${stacks} stacks)`);
    const nextStacks = stacks - 1;
    const statuses =
      nextStacks > 0
        ? enemy.statuses.map((s) =>
            s.id === 'bleed' ? { ...s, intensity: nextStacks } : s,
          )
        : enemy.statuses.filter((s) => s.id !== 'bleed');
    return {
      ...enemy,
      hp: hpAfter,
      statuses,
    };
  });
}

/**
 * If pending level-ups exist, open a skill offer and pause combat.
 */
export function beginLevelUpIfNeeded(
  state: EncounterState,
  rng: SeededRng,
  resumePhase: EncounterState['phaseAfterLevelUp'],
): EncounterState {
  if (state.progress.pendingLevelUps <= 0) {
    return state;
  }
  const progress = rollSkillOffers(
    state.progress,
    state.tray,
    rng,
    3,
    state.unlockedActiveSkillIds,
    state.equipmentSnapshot ?? null,
  );
  return {
    ...state,
    phase: 'levelUp',
    phaseAfterLevelUp: resumePhase,
    progress,
    log: [
      ...state.log,
      state.progress.awaitingFirstPrimary
        ? 'Level up — choose a primary dice skill'
        : 'Level up — choose a skill',
    ],
  };
}

export function createEncounter(
  rng: SeededRng,
  options: CreateEncounterOptions = {},
): EncounterState {
  const unlocked = options.unlockedDiceSlots ?? STARTING_UNLOCKED_DICE_SLOTS;
  const templateIds =
    options.enemyTemplateIds ?? DEFAULT_ENCOUNTER_TEMPLATE_IDS;
  const enemies = templateIds.map((templateId, index) =>
    createEnemyFromTemplate(templateId, `${templateId}-${index}`, rng, {
      dungeonLevel: options.dungeonLevel ?? 1,
      mutation: options.mutation ?? 'standard',
    }),
  );
  const combatStats = options.combatStats ?? defaultCharacterCombatStats();
  const unlockedActiveSkillIds = [
    ...(options.unlockedActiveSkillIds ?? combatStats.unlockedActiveSkillIds),
  ];
  const playerMaxHp =
    options.playerMaxHp ?? combatStats.maxHp ?? PROVISIONAL_PLAYER_MAX_HP;
  const startingHp =
    options.playerHp !== undefined
      ? Math.min(Math.max(0, options.playerHp), playerMaxHp)
      : playerMaxHp;
  // Preserve skill assignments; clear faces/holds for a fresh encounter auto-roll.
  const tray = options.tray
    ? clearDiceFaces(options.tray)
    : createDiceTray(unlocked);
  const rawProgress = options.progress ?? createTemporaryProgress();
  const progress: EncounterState['progress'] = {
    ...createTemporaryProgress(),
    ...rawProgress,
    selectedEnhancementIds: rawProgress.selectedEnhancementIds ?? [],
  };

  const state: EncounterState = {
    turn: 1,
    phase: 'rolling',
    player: {
      name: options.playerName ?? 'Vanguard',
      maxHp: playerMaxHp,
      hp: startingHp,
      // Shield/statuses are encounter-scoped; HP and temp skills persist.
      shield: 0,
      statuses: [],
    },
    enemies,
    selectedTargetId: defaultTargetId(enemies),
    tray,
    hasRolledThisTurn: false,
    rerollAllRemaining:
      combatStats.fullRerollsPerTurn ?? REROLL_ALL_PER_TURN,
    rerollSingleRemaining:
      combatStats.targetedRerollsPerTurn ?? REROLL_SINGLE_PER_TURN,
    eligibleCombos: [],
    selectedComboId: null,
    progress,
    phaseAfterLevelUp: null,
    unlockedActiveSkillIds,
    combatStats,
    equipmentSnapshot: options.equipmentSnapshot ?? null,
    dungeonLevel: options.dungeonLevel ?? 1,
    mutation: options.mutation ?? 'standard',
    log: [`Encounter start — ${enemies.map((e) => e.name).join(' & ')}`],
    lastEvents: [{ type: 'turn_started', turn: 1 }],
  };
  // Opening faces are rolled immediately so combat starts at hold/reroll/attack.
  return rollPlayerDice(state, rng);
}

export function selectTarget(
  state: EncounterState,
  targetId: string,
): EncounterState {
  if (
    state.phase === 'won' ||
    state.phase === 'lost' ||
    state.phase === 'levelUp'
  ) {
    return state;
  }
  const target = livingEnemies(state.enemies).find((e) => e.id === targetId);
  if (!target) {
    throw new Error(`Cannot target enemy ${targetId}`);
  }
  return { ...state, selectedTargetId: targetId };
}

export function selectCombo(
  state: EncounterState,
  comboId: ComboId,
): EncounterState {
  if (state.phase !== 'comboSelection' && state.phase !== 'rolling') {
    return state;
  }
  if (!state.hasRolledThisTurn) {
    throw new Error('Cannot select combo before rolling');
  }
  const match = getComboById(state.eligibleCombos, comboId);
  if (!match) {
    throw new Error(`Combo ${comboId} is not eligible`);
  }
  return { ...state, selectedComboId: comboId, phase: 'comboSelection' };
}

export function rollPlayerDice(
  state: EncounterState,
  rng: SeededRng,
): EncounterState {
  if (state.phase !== 'rolling' || state.hasRolledThisTurn) {
    throw new Error('Initial roll is only allowed once at the start of the turn');
  }
  const tray = rollUnlockedDice(state.tray, rng);
  return withCombos({
    ...state,
    tray,
    hasRolledThisTurn: true,
    phase: 'comboSelection',
    log: [...state.log, 'Rolled unlocked dice'],
    lastEvents: [],
  });
}

export function holdDie(
  state: EncounterState,
  dieId: number,
): EncounterState {
  if (!state.hasRolledThisTurn) {
    throw new Error('Cannot hold before rolling');
  }
  if (state.phase !== 'rolling' && state.phase !== 'comboSelection') {
    throw new Error('Cannot hold dice during resolution');
  }
  const tray = toggleDieHold(state.tray, dieId);
  return { ...state, tray, lastEvents: [] };
}

export function combatRerollAll(
  state: EncounterState,
  rng: SeededRng,
): EncounterState {
  if (!state.hasRolledThisTurn) {
    throw new Error('Reroll All requires an initial roll');
  }
  if (state.phase !== 'comboSelection' && state.phase !== 'rolling') {
    throw new Error('Cannot reroll during resolution');
  }
  if (state.rerollAllRemaining <= 0) {
    throw new Error('No Reroll All remaining this turn');
  }
  const tray = rerollUnheldDice(state.tray, rng);
  return withCombos({
    ...state,
    tray,
    rerollAllRemaining: state.rerollAllRemaining - 1,
    phase: 'comboSelection',
    log: [
      ...state.log,
      `Reroll All (${state.rerollAllRemaining - 1} left)`,
    ],
    lastEvents: [],
  });
}

export function combatRerollSingle(
  state: EncounterState,
  dieId: number,
  rng: SeededRng,
): EncounterState {
  if (!state.hasRolledThisTurn) {
    throw new Error('Reroll Single requires an initial roll');
  }
  if (state.phase !== 'comboSelection' && state.phase !== 'rolling') {
    throw new Error('Cannot reroll during resolution');
  }
  if (state.rerollSingleRemaining <= 0) {
    throw new Error('No Reroll Single remaining this turn');
  }
  const tray = rerollSingleDie(state.tray, dieId, rng);
  return withCombos({
    ...state,
    tray,
    rerollSingleRemaining: state.rerollSingleRemaining - 1,
    phase: 'comboSelection',
    log: [
      ...state.log,
      `Reroll Single die ${dieId} (${state.rerollSingleRemaining - 1} left)`,
    ],
    lastEvents: [],
  });
}

function computeAttackDamage(
  state: EncounterState,
  comboId: ComboId,
  target: EnemyState,
  rng?: SeededRng,
  options?: {
    extraCritChancePp?: number;
  },
): {
  damage: number;
  comboName: string;
  participating: number[];
  faceSum: number;
  multiplier: number;
  isCrit: boolean;
  baseComboDamage: number;
  consumedVulnerable: boolean;
} {
  const combo = getComboById(state.eligibleCombos, comboId);
  if (!combo) {
    throw new Error(`Selected combo ${comboId} is not eligible`);
  }
  const stats = state.combatStats ?? defaultCharacterCombatStats();
  const attack = computeComboAttack(
    state.tray,
    combo.definition,
    combo.participatingDieIds,
    stats.weaponSwingDamage ?? 0,
  );
  // Option C (approved): class flat Attack after combo floor, before Attack %.
  // Applied once per primary attack; never combo-multiplied or OH-penalised.
  const prePercentBase = combineComboAndClassFlatAttack(
    attack.damage,
    stats.flatAttack ?? 0,
  );
  let damage = applyOutgoingDamageBonuses(prePercentBase, stats, {
    targetHp: target.hp,
    targetMaxHp: target.maxHp,
    targetIsBleeding: target.statuses.some((s) => s.id === 'bleed'),
  });

  // Vulnerable: each stack +10% to next successful attack, then consume all.
  const vuln = target.statuses.find((s) => s.id === 'vulnerable');
  let consumedVulnerable = false;
  if (vuln && vuln.intensity > 0) {
    damage = Math.floor(damage * (1 + (vuln.intensity * 10) / 100));
    consumedVulnerable = true;
  }

  let isCrit = false;
  if (rng) {
    const crit = rollCritDamage(damage, stats, rng.next(), {
      extraCritChancePp: options?.extraCritChancePp ?? 0,
    });
    damage = crit.damage;
    isCrit = crit.isCrit;
  }

  return {
    damage,
    comboName: combo.definition.name,
    participating: combo.participatingDieIds,
    faceSum: attack.faceSum,
    multiplier: attack.multiplier,
    isCrit,
    // Combo-only base (no class flat). Flurry uses this — see Stage B review gate.
    baseComboDamage: attack.damage,
    consumedVulnerable,
  };
}

function applyBleedChanceOnHit(
  enemy: EnemyState,
  stats: CharacterCombatStats,
  rng: SeededRng | undefined,
): EnemyState {
  if (!rng || stats.bleedChancePp <= 0 || enemy.hp <= 0) {
    return enemy;
  }
  const chance = Math.min(100, stats.bleedChancePp) / 100;
  if (rng.next() >= chance) {
    return enemy;
  }
  const existing = enemy.statuses.find((s) => s.id === 'bleed');
  const statuses = existing
    ? enemy.statuses.map((s) =>
        s.id === 'bleed' ? { ...s, intensity: s.intensity + 1 } : s,
      )
    : [...enemy.statuses, { id: 'bleed' as const, intensity: 1, remainingTurns: 99 }];
  return { ...enemy, statuses };
}

function awardDefeatXp(
  state: EncounterState,
  enemy: EnemyState,
  events: CombatEvent[],
): EncounterState {
  const xpGained = xpForEnemyTemplate(enemy.templateId);
  const progress = gainTemporaryXp(state.progress, xpGained);
  events.push({
    type: 'enemy_defeated',
    enemyId: enemy.id,
    xpGained,
  });
  events.push({
    type: 'xp_gained',
    amount: xpGained,
    level: progress.level,
    xp: progress.xp,
    pendingLevelUps: progress.pendingLevelUps,
  });
  if (progress.pendingLevelUps > state.progress.pendingLevelUps) {
    for (
      let lvl = state.progress.level + 1;
      lvl <= progress.level;
      lvl += 1
    ) {
      events.push({ type: 'level_up', newLevel: lvl });
    }
  }
  const leveled = progress.pendingLevelUps > state.progress.pendingLevelUps;
  return {
    ...state,
    progress,
    log: [
      ...state.log,
      `${enemy.name} defeated (+${xpGained} XP)`,
      leveled
        ? `Reached temporary level ${progress.level}`
        : `XP ${progress.xp} (Lv ${progress.level})`,
    ],
  };
}

/**
 * Resolves the player's committed attack against the selected target, then
 * participating dice skills left→right. May pause on levelUp after kills.
 */
export function resolvePlayerAttack(
  state: EncounterState,
  rng?: SeededRng,
): EncounterState {
  if (state.phase === 'won' || state.phase === 'lost') {
    return state;
  }
  if (state.phase === 'levelUp') {
    throw new Error('Cannot attack during skill selection');
  }
  if (!state.hasRolledThisTurn || !state.selectedComboId) {
    throw new Error('Cannot attack without a rolled combo');
  }
  const targetId = state.selectedTargetId;
  if (!targetId) {
    throw new Error('No target selected');
  }
  const target = livingEnemies(state.enemies).find((e) => e.id === targetId);
  if (!target) {
    throw new Error('Selected target is not alive');
  }

  const stats = state.combatStats ?? defaultCharacterCombatStats();
  const combo = getComboById(state.eligibleCombos, state.selectedComboId);
  if (!combo) {
    throw new Error(`Selected combo ${state.selectedComboId} is not eligible`);
  }
  const participating = combo.participatingDieIds;
  const selectedEnh =
    state.progress.selectedEnhancementIds ?? [];
  const skillPlan = planParticipatingSkills(
    state.tray,
    participating,
    selectedEnh,
  );

  // Shield Breaker strips accumulated shield before the primary hit.
  let preHitEnemies = state.enemies;
  if (skillPlan.stripShieldBeforeDamage) {
    preHitEnemies = preHitEnemies.map((e) =>
      e.id === targetId ? { ...e, shield: 0 } : e,
    );
  }
  const preHitTarget =
    preHitEnemies.find((e) => e.id === targetId) ?? target;

  const {
    damage: rolledDamage,
    comboName,
    faceSum,
    multiplier,
    isCrit,
    baseComboDamage,
    consumedVulnerable,
  } = computeAttackDamage(
    { ...state, enemies: preHitEnemies },
    state.selectedComboId,
    preHitTarget,
    rng,
  );

  // Next-attack % bonuses (Cleave/Rend/Sunder/Shield Breaker) stack additively
  // onto the activating attack — one unified primary hit, not a follow-up strike.
  const damage =
    skillPlan.primaryDamageBonusPct > 0
      ? Math.floor(
          (rolledDamage * (100 + skillPlan.primaryDamageBonusPct)) / 100,
        )
      : rolledDamage;

  let enemyShield = preHitTarget.shield ?? 0;
  let remainingDmg = damage;
  if (enemyShield > 0) {
    const absorbed = Math.min(enemyShield, remainingDmg);
    enemyShield -= absorbed;
    remainingDmg -= absorbed;
  }
  const mitigated = Math.max(0, remainingDmg - preHitTarget.intention.guardAmount);
  const hpAfter = Math.max(0, preHitTarget.hp - mitigated);
  const guardBlocked = Math.min(remainingDmg, preHitTarget.intention.guardAmount);

  const events: CombatEvent[] = [
    {
      type: 'player_attack',
      targetId,
      comboId: state.selectedComboId,
      comboName,
      damage: mitigated,
      targetHpAfter: hpAfter,
      isCrit,
    },
  ];

  // Attack-sequence crit streak: primary → Flurry/Twin Strike → Attack Again.
  let attackSequence = recordAttackCritOutcome(
    createAttackSequenceCritState(),
    isCrit,
  );

  let enemies = preHitEnemies.map((enemy) => {
    if (enemy.id !== targetId) return enemy;
    let updated: EnemyState = {
      ...enemy,
      hp: hpAfter,
      shield: enemyShield,
      statuses: consumedVulnerable
        ? enemy.statuses.filter((s) => s.id !== 'vulnerable')
        : [...enemy.statuses],
    };
    if (mitigated > 0) {
      updated = applyBleedChanceOnHit(updated, stats, rng);
    }
    return updated;
  });
  let player = { ...state.player, statuses: [...state.player.statuses] };
  let totalDamageThisActivation = mitigated;

  // Rend: guaranteed bleed stacks on the primary hit (plus ordinary bleed chance).
  if (skillPlan.guaranteedBleedStacks > 0 && hpAfter >= 0) {
    enemies = applyStatusToEnemy(
      enemies,
      targetId,
      'bleed',
      skillPlan.guaranteedBleedStacks,
      99,
    );
  }

  // Sunder: apply 1 Vulnerable after the hit (consumption already handled above).
  if (skillPlan.applyVulnerableStacks > 0 && hpAfter > 0) {
    enemies = applyStatusToEnemy(
      enemies,
      targetId,
      'vulnerable',
      skillPlan.applyVulnerableStacks,
      99,
    );
  }

  // Cleave: splash every other living enemy for % of actual primary damage dealt.
  if (
    skillPlan.cleaveSplashPctOfPrimary != null &&
    skillPlan.cleaveSplashPctOfPrimary > 0 &&
    mitigated > 0
  ) {
    const splash = applyCleaveSplash(
      enemies,
      targetId,
      mitigated,
      skillPlan.cleaveSplashPctOfPrimary,
    );
    enemies = splash.enemies;
    totalDamageThisActivation += splash.totalSplash;
  }

  // Bloodlust: heal once when the activating attack dealt damage.
  if (
    skillPlan.bloodlustHealMaxHpPct != null &&
    skillPlan.bloodlustHealMaxHpPct > 0 &&
    mitigated > 0
  ) {
    const heal = Math.max(
      1,
      Math.floor(player.maxHp * (skillPlan.bloodlustHealMaxHpPct / 100)),
    );
    player = {
      ...player,
      hp: Math.min(player.maxHp, player.hp + heal),
    };
  }

  const critNote = isCrit ? ' CRIT' : '';
  const bonusNote =
    skillPlan.primaryDamageBonusPct > 0
      ? ` (+${skillPlan.primaryDamageBonusPct}% skills)`
      : '';
  let next: EncounterState = pushLog(
    {
      ...state,
      phase: 'resolving',
      enemies,
      player,
      lastEvents: events,
    },
    formatPlayerAttackLog({
      comboName: `${comboName}${critNote}${bonusNote}`,
      targetName: target.name,
      damageDealt: mitigated,
      faceSum,
      multiplier,
      targetHpAfter: hpAfter,
      targetMaxHp: target.maxHp,
      guardBlocked: guardBlocked > 0 ? guardBlocked : undefined,
    }),
  );

  // Emit next-attack skill events (modifiers on the primary hit — no extra strike).
  for (const activation of skillPlan.nextAttackActivations) {
    events.push({
      type: 'skill_activated',
      dieId: activation.dieId,
      skillId: activation.skillId,
      skillName: activation.skillName,
      skillLevel: activation.skillLevel,
      summary: activation.summary,
      damage: 0,
      statusApplied: activation.statusApplied,
      targetId,
    });
    next = {
      ...next,
      log: [...next.log, `Skill: ${activation.summary}`],
      lastEvents: [...events],
    };
  }

  if (hpAfter <= 0) {
    next = awardDefeatXp(next, { ...target, hp: 0 }, events);
    next = { ...next, lastEvents: events };
  }
  // Splash kills
  for (const enemy of enemies) {
    const before = preHitEnemies.find((e) => e.id === enemy.id);
    if (before && before.hp > 0 && enemy.hp <= 0 && enemy.id !== targetId) {
      next = awardDefeatXp(next, enemy, events);
      next = { ...next, lastEvents: [...events] };
    }
  }
  enemies = next.enemies.map((e) => ({ ...e, statuses: [...e.statuses] }));

  // Post-attack skills (Flurry / Twin Strike / Iron Harvest / Kinetic Cascade /
  // provisional M3 skills) still resolve left → right after the primary hit.
  for (const { dieId, skill, level } of skillPlan.postAttackSkills) {
    const living = livingEnemies(next.enemies);
    const skillTargetId = living.some((e) => e.id === next.selectedTargetId)
      ? next.selectedTargetId
      : (living[0]?.id ?? null);

    const twinCritPp = selectedEnh.includes('precise_twin_strike') ? 15 : 0;
    const twinDmgPct = selectedEnh.includes('deadly_twin_strike') ? 25 : 0;
    const result = executePrimarySkill(
      skill,
      level,
      next.player,
      next.enemies,
      skillTargetId,
      {
        combatStats: stats,
        activatingAttackDamage: baseComboDamage,
        damageAlreadyDealt: totalDamageThisActivation,
        rng,
        attackSequence,
        twinStrikeExtraCritChancePp:
          skill.id === 'twin_strike' ? twinCritPp : undefined,
        twinStrikeDamageBonusPct:
          skill.id === 'twin_strike' ? twinDmgPct : undefined,
      },
    );
    totalDamageThisActivation += result.totalDamageDealt ?? result.damage;
    if (result.attackSequence) {
      attackSequence = result.attackSequence;
    }
    next = {
      ...next,
      player: result.player,
      enemies: result.enemies,
      log: [...next.log, `Skill: ${result.summary}`],
    };
    events.push({
      type: 'skill_activated',
      dieId,
      skillId: skill.id,
      skillName: skill.name,
      skillLevel: level,
      summary: result.summary,
      damage: result.damage,
      shieldGained: result.shieldGained,
      statusApplied: result.statusApplied,
      targetId: result.targetId,
    });
    for (const hit of result.chainedHits ?? []) {
      events.push({
        type: 'chained_attack',
        source: hit.source,
        targetId: hit.targetId,
        damage: hit.damage,
        isCrit: hit.isCrit,
        targetHpAfter: hit.targetHpAfter,
      });
    }
    next = { ...next, lastEvents: [...events] };

    for (const enemy of result.enemies) {
      const before = enemies.find((e) => e.id === enemy.id);
      if (before && before.hp > 0 && enemy.hp <= 0) {
        next = awardDefeatXp(next, enemy, events);
        next = { ...next, lastEvents: [...events] };
      }
    }
    enemies = next.enemies.map((e) => ({ ...e, statuses: [...e.statuses] }));
  }

  // Chance to Attack Again — cannot itself retrigger (single extra only).
  if (
    rng &&
    stats.chanceToAttackAgainPp > 0 &&
    livingEnemies(next.enemies).length > 0
  ) {
    const chance = Math.min(100, stats.chanceToAttackAgainPp) / 100;
    if (rng.next() < chance) {
      const living = livingEnemies(next.enemies);
      const againTarget =
        living.find((e) => e.id === next.selectedTargetId) ?? living[0]!;
      const consecutivePp = consecutiveCritBonusPp({
        consecutiveCritsSoFar: attackSequence.consecutiveCrits,
        talentPp: stats.critChanceOnConsecutiveHitsPp,
      });
      const again = computeAttackDamage(
        next,
        state.selectedComboId,
        againTarget,
        rng,
        { extraCritChancePp: consecutivePp },
      );
      let sh = againTarget.shield ?? 0;
      let rem = again.damage;
      if (sh > 0) {
        const ab = Math.min(sh, rem);
        sh -= ab;
        rem -= ab;
      }
      const dealt = Math.max(0, rem - againTarget.intention.guardAmount);
      const hp2 = Math.max(0, againTarget.hp - dealt);
      attackSequence = recordAttackCritOutcome(attackSequence, again.isCrit);
      const critNote = again.isCrit ? ' CRIT' : '';
      next = {
        ...next,
        enemies: next.enemies.map((e) => {
          if (e.id !== againTarget.id) return e;
          let u: EnemyState = { ...e, hp: hp2, shield: sh };
          if (dealt > 0) u = applyBleedChanceOnHit(u, stats, rng);
          return u;
        }),
        log: [
          ...next.log,
          `Attack Again${critNote} → ${againTarget.name} for ${dealt}`,
        ],
      };
      events.push({
        type: 'chained_attack',
        source: 'attack_again',
        targetId: againTarget.id,
        damage: dealt,
        isCrit: again.isCrit,
        targetHpAfter: hp2,
      });
      next = { ...next, lastEvents: [...events] };
      if (hp2 <= 0) {
        next = awardDefeatXp(next, { ...againTarget, hp: 0 }, events);
      }
    }
  }
  // Attack sequence ends here — consecutive-crit streak does not persist.

  if (livingEnemies(next.enemies).length === 0) {
    events.push({ type: 'victory' });
    next = {
      ...next,
      phase: 'won',
      lastEvents: events,
      log: [...next.log, 'Victory!'],
    };
    if (next.progress.pendingLevelUps > 0 && rng) {
      return beginLevelUpIfNeeded(next, rng, 'won');
    }
    return next;
  }

  const selectedTargetId = livingEnemies(next.enemies).some(
    (e) => e.id === targetId,
  )
    ? targetId
    : defaultTargetId(next.enemies);

  next = {
    ...next,
    selectedTargetId,
    phase: 'enemyActions',
    lastEvents: events,
  };

  if (next.progress.pendingLevelUps > 0 && rng) {
    return beginLevelUpIfNeeded(next, rng, 'enemyActions');
  }
  return next;
}

/**
 * Surviving enemies act individually in encounter order, then a new player
 * turn begins (fresh dice, refreshed combat reroll budgets, new intentions).
 * Stunned enemies skip their action. Bleed ticks before intentions resolve.
 */
export function resolveEnemyActions(
  state: EncounterState,
  rng: SeededRng,
): EncounterState {
  if (state.phase === 'won' || state.phase === 'lost') {
    return state;
  }
  if (state.phase === 'levelUp') {
    throw new Error('Cannot resolve enemy actions during skill selection');
  }
  if (state.phase !== 'enemyActions' && state.phase !== 'resolving') {
    throw new Error('Enemy actions resolve only after the player attack');
  }

  const events: CombatEvent[] = [...state.lastEvents];
  let playerHp = state.player.hp;
  let playerShield = state.player.shield;
  const log = [...state.log];
  const stats = state.combatStats ?? defaultCharacterCombatStats();

  let enemies = applyBleedTicks(
    state.enemies,
    events,
    log,
    stats.bleedDamagePct,
  );

  // Enemies killed by bleed grant XP.
  let progress = state.progress;
  for (const enemy of enemies) {
    const before = state.enemies.find((e) => e.id === enemy.id);
    if (before && before.hp > 0 && enemy.hp <= 0) {
      const xpGained = xpForEnemyTemplate(enemy.templateId);
      progress = gainTemporaryXp(progress, xpGained);
      events.push({
        type: 'enemy_defeated',
        enemyId: enemy.id,
        xpGained,
      });
      log.push(`${enemy.name} defeated (+${xpGained} XP)`);
    }
  }

  if (livingEnemies(enemies).length === 0) {
    events.push({ type: 'victory' });
    let next: EncounterState = {
      ...state,
      phase: 'won',
      enemies,
      progress,
      player: { ...state.player, hp: playerHp, shield: playerShield },
      lastEvents: events,
      log: [...log, 'Victory!'],
    };
    if (next.progress.pendingLevelUps > 0) {
      return beginLevelUpIfNeeded(next, rng, 'won');
    }
    return next;
  }

  for (const enemy of livingEnemies(enemies)) {
    const stunned = enemy.statuses.some((s) => s.id === 'stun');
    if (stunned) {
      events.push({ type: 'enemy_stunned', enemyId: enemy.id });
      log.push(`${enemy.name} is stunned and skips its action`);
      enemies = enemies.map((e) =>
        e.id === enemy.id
          ? {
              ...e,
              statuses: e.statuses
                .map((s) =>
                  s.id === 'bleed'
                    ? s
                    : { ...s, remainingTurns: s.remainingTurns - 1 },
                )
                .filter((s) => s.remainingTurns > 0),
            }
          : e,
      );
      continue;
    }

    if (
      (enemy.intention.kind === 'strike' ||
        enemy.intention.kind === 'special') &&
      enemy.intention.damage > 0
    ) {
      let remaining = mitigateIncomingDamage(enemy.intention.damage, stats);
      let shielded = 0;
      let fullyBlocked = false;
      let fullyDodged = false;

      // GDD: dodge negates eligible attack damage and statuses.
      // Undodgeable boss telegraphs TBD — no intention flag yet.
      const dodgeChance = Math.min(100, stats.dodgeChancePp ?? 0) / 100;
      if (dodgeChance > 0 && rng.next() < dodgeChance) {
        fullyDodged = true;
        remaining = 0;
        log.push(`${enemy.name} ${enemy.intention.label} → DODGED`);
      }

      // Spec block: successful Block negates 100% of incoming attack.
      const blockChance = Math.min(100, stats.blockChancePp) / 100;
      if (!fullyDodged && blockChance > 0 && rng.next() < blockChance) {
        fullyBlocked = true;
        remaining = 0;
        log.push(`${enemy.name} ${enemy.intention.label} → BLOCKED`);
      }

      if (!fullyDodged && !fullyBlocked && playerShield > 0 && remaining > 0) {
        shielded = Math.min(playerShield, remaining);
        playerShield -= shielded;
        remaining -= shielded;
      }
      playerHp = Math.max(0, playerHp - remaining);
      events.push({
        type: 'enemy_attack',
        enemyId: enemy.id,
        damage: remaining,
        playerHpAfter: playerHp,
        blocked:
          fullyBlocked || fullyDodged ? enemy.intention.damage : shielded,
        dodged: fullyDodged,
      });
      if (!fullyDodged && !fullyBlocked) {
        log.push(
          `${enemy.name} ${enemy.intention.label} → hero ${remaining} dmg` +
            (shielded > 0 ? ` (${shielded} shielded)` : '') +
            ` · HP ${playerHp}/${state.player.maxHp}`,
        );
      }

      // Counterattack: can proc on blocked or dodged attacks (VANGUARD_FINAL_SPEC).
      let counterChance = stats.counterChancePp;
      if (fullyBlocked) {
        counterChance += stats.counterChanceWhenBlockingPp;
      }
      if (enemy.hp > 0 && counterChance > 0 && rng.next() < counterChance / 100) {
        let counterDmg = Math.max(1, Math.floor(state.player.maxHp * 0.05));
        // OPEN provisional counter base (no weapon swing defined).
        if (fullyBlocked && stats.counterDamageAfterBlockingPct > 0) {
          counterDmg = Math.floor(
            counterDmg * (1 + stats.counterDamageAfterBlockingPct / 100),
          );
        }
        counterDmg = applyOutgoingDamageBonuses(counterDmg, stats, {
          targetHp: enemy.hp,
          targetMaxHp: enemy.maxHp,
          targetIsBleeding: enemy.statuses.some((s) => s.id === 'bleed'),
        });
        const hpAfterCounter = Math.max(0, enemy.hp - counterDmg);
        enemies = enemies.map((e) =>
          e.id === enemy.id ? { ...e, hp: hpAfterCounter } : e,
        );
        log.push(`Counterattack → ${enemy.name} for ${counterDmg}`);
        if (hpAfterCounter <= 0) {
          const xpGained = xpForEnemyTemplate(enemy.templateId);
          progress = gainTemporaryXp(progress, xpGained);
          events.push({
            type: 'enemy_defeated',
            enemyId: enemy.id,
            xpGained,
          });
          log.push(`${enemy.name} defeated (+${xpGained} XP)`);
        }
      }
    } else if (enemy.intention.kind === 'guard') {
      log.push(`${enemy.name} braces (${enemy.intention.label})`);
    }

    // Tick non-bleed statuses after the enemy acts (bleed duration already consumed).
    enemies = enemies.map((e) =>
      e.id === enemy.id
        ? {
            ...e,
            statuses: e.statuses
              .map((s) =>
                s.id === 'bleed'
                  ? s
                  : { ...s, remainingTurns: s.remainingTurns - 1 },
              )
              .filter((s) => s.remainingTurns > 0),
          }
        : e,
    );

    if (playerHp <= 0) {
      events.push({ type: 'defeat' });
      return {
        ...state,
        phase: 'lost',
        enemies,
        progress,
        player: {
          ...state.player,
          hp: 0,
          shield: playerShield,
        },
        lastEvents: events,
        log: [...log, 'Defeat…'],
      };
    }
  }

  const turn = state.turn + 1;
  enemies = enemies.map((enemy) =>
    enemy.hp > 0
      ? refreshEnemyIntention(enemy, rng, {
          dungeonLevel: state.dungeonLevel ?? 1,
          mutation: state.mutation ?? 'standard',
        })
      : enemy,
  );
  events.push({ type: 'turn_started', turn });

  let nextTurn: EncounterState = {
    ...state,
    turn,
    phase: 'rolling',
    player: {
      ...state.player,
      hp: playerHp,
      shield: playerShield,
      statuses: tickStatuses(state.player.statuses),
    },
    enemies,
    progress,
    selectedTargetId: defaultTargetId(enemies),
    tray: clearDiceFaces(state.tray),
    hasRolledThisTurn: false,
    rerollAllRemaining:
      stats.fullRerollsPerTurn ?? REROLL_ALL_PER_TURN,
    rerollSingleRemaining:
      stats.targetedRerollsPerTurn ?? REROLL_SINGLE_PER_TURN,
    eligibleCombos: [],
    selectedComboId: null,
    phaseAfterLevelUp: null,
    lastEvents: events,
    log: [...log, `Turn ${turn}`],
  };

  if (nextTurn.progress.pendingLevelUps > 0) {
    // Level-ups from bleed kills before the new turn — pause first.
    nextTurn = beginLevelUpIfNeeded(nextTurn, rng, 'rolling');
    if (nextTurn.phase === 'levelUp') {
      return nextTurn;
    }
  }

  return rollPlayerDice(nextTurn, rng);
}

export function commitPlayerAttack(
  state: EncounterState,
  rng: SeededRng,
): EncounterState {
  const afterPlayer = resolvePlayerAttack(state, rng);
  if (
    afterPlayer.phase === 'won' ||
    afterPlayer.phase === 'lost' ||
    afterPlayer.phase === 'levelUp'
  ) {
    return afterPlayer;
  }
  return resolveEnemyActions(afterPlayer, rng);
}

/**
 * Rerolls the three skill offers using a dungeon skill-offer reroll.
 */
export function combatRerollSkillOffers(
  state: EncounterState,
  rng: SeededRng,
): EncounterState {
  if (state.phase !== 'levelUp') {
    throw new Error('Skill offers can only be rerolled during level-up');
  }
  const progress = rerollSkillOffers(
    state.progress,
    state.tray,
    rng,
    state.unlockedActiveSkillIds,
    state.equipmentSnapshot ?? null,
  );
  return {
    ...state,
    progress,
    log: [
      ...state.log,
      `Skill offer rerolled (${progress.skillOfferRerollsRemaining} left)`,
    ],
  };
}

/**
 * Accepts a skill from the current offer. New skills require `targetDieId`.
 * When die selection is required and omitted, returns state unchanged.
 */
export function chooseSkillFromOffer(
  state: EncounterState,
  skillId: string,
  rng: SeededRng,
  targetDieId?: number,
): EncounterState {
  if (state.phase !== 'levelUp') {
    throw new Error('Skills can only be chosen during level-up');
  }
  const result = acceptSkillOffer(
    state.progress,
    state.tray,
    skillId,
    targetDieId,
  );
  if (result.needsDieSelection) {
    return state;
  }

  const offer = state.progress.currentOffer?.find(
    (o) => o.offerId === skillId || o.skillId === skillId,
  );
  const logLine =
    offer?.kind === 'enhancement'
      ? `Enhancement: ${offer.displayName ?? skillId}${
          offer.exclusionNote ? ` (${offer.exclusionNote})` : ''
        }`
      : targetDieId !== undefined
        ? `Learned ${getSkillDefinition(result.skillId).name} on die ${targetDieId + 1}`
        : `Upgraded ${getSkillDefinition(result.skillId).name}`;

  let next: EncounterState = {
    ...state,
    tray: result.tray,
    progress: result.progress,
    log: [...state.log, logLine],
  };

  // Chain additional pending level-ups.
  if (next.progress.pendingLevelUps > 0) {
    return beginLevelUpIfNeeded(next, rng, next.phaseAfterLevelUp);
  }

  const resume = next.phaseAfterLevelUp ?? 'comboSelection';
  next = {
    ...next,
    phase: resume === 'rolling' ? 'rolling' : resume,
    phaseAfterLevelUp: null,
  };

  if (next.phase === 'won' || next.phase === 'lost') {
    return next;
  }
  if (next.phase === 'enemyActions' || next.phase === 'resolving') {
    return resolveEnemyActions({ ...next, phase: 'enemyActions' }, rng);
  }
  if (next.phase === 'rolling' && !next.hasRolledThisTurn) {
    return rollPlayerDice(next, rng);
  }
  return next;
}

/**
 * True when accepting `skillId` requires the player to pick a target die.
 * Upgrades apply on the owned die automatically; new skills always need a die.
 */
export function skillChoiceNeedsDie(
  state: EncounterState,
  skillId: string,
): boolean {
  if (state.phase !== 'levelUp' || !state.progress.currentOffer) {
    return false;
  }
  const offer = state.progress.currentOffer.find(
    (o) => o.offerId === skillId || o.skillId === skillId,
  );
  if (!offer || offer.isUpgrade || offer.kind === 'enhancement') {
    return false;
  }
  return true;
}

/** Dev helper: unlock additional dice slots mid-run without permanent talents. */
export function devUnlockDiceSlots(
  state: EncounterState,
  unlockedCount: number,
): EncounterState {
  const tray = createDiceTray(unlockedCount);
  // Preserve faces/holds/skills from overlapping unlocked slots.
  const dice = tray.dice.map((die) => {
    const prev = state.tray.dice[die.id];
    if (!prev || !die.unlocked) {
      return die;
    }
    return {
      ...die,
      face: prev.unlocked ? prev.face : undefined,
      held: prev.unlocked ? prev.held : false,
      primarySkillId: prev.unlocked ? prev.primarySkillId : null,
      primarySkillLevel: prev.unlocked ? prev.primarySkillLevel : 0,
    };
  });
  const next = withCombos({
    ...state,
    tray: { dice, unlockedCount },
  });
  return next;
}
